import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  MapPin, 
  Mail, 
  Linkedin,
  ArrowRight
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { subscribeNewsletter } from '../../lib/zoho';

const quickLinks = [
  { name: 'About Us', href: '/about' },
  { name: 'Services', href: '/services' },
  { name: 'Contact', href: '/about#contact-form' },
];

const Footer = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: ''
  });
  const [formErrors, setFormErrors] = useState({
    fullName: '',
    email: ''
  });
  const [status, setStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = () => {
    const errors = {
      fullName: '',
      email: ''
    };
    let isValid = true;

    if (!formData.fullName.trim()) {
      errors.fullName = 'Full name is required';
      isValid = false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
      isValid = false;
    } else if (!emailRegex.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setStatus({ type: null, message: '' });

    try {
      await subscribeNewsletter(formData);
      setStatus({
        type: 'success',
        message: 'Successfully subscribed to newsletter!'
      });
      setFormData({ fullName: '', email: '' });
      setFormErrors({ fullName: '', email: '' });
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'Failed to subscribe. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-black border-t border-white/10">
      <div className="container mx-auto px-4">
        {/* Main Footer Content */}
        <div className="py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-[2fr_1fr_1.5fr] gap-12 lg:gap-16">
          {/* Company Info */}
          <div className="space-y-8">
            <svg 
              className="h-8 w-auto text-white" 
              viewBox="0 0 786.37 93.72"
              fill="currentColor"
            >
              <defs>
                <linearGradient id="logo-gradient-footer" x1="651.27" y1="4.53" x2="743.32" y2="96.58" gradientUnits="userSpaceOnUse">
                  <stop offset="0" stopColor="#1cb899"/>
                  <stop offset=".5" stopColor="#134aa0"/>
                  <stop offset=".56" stopColor="#26459c"/>
                  <stop offset=".69" stopColor="#593992"/>
                  <stop offset=".86" stopColor="#aa2782"/>
                  <stop offset="1" stopColor="#ed1876"/>
                </linearGradient>
              </defs>
              <path className="fill-white" d="M12.64,80.99c-5.17-1.39-9.39-3.18-12.64-5.37l7.76-14.73c3.51,2.26,7.26,3.96,11.24,5.12,3.98,1.16,7.96,1.74,11.94,1.74s6.71-.55,8.8-1.64c2.09-1.09,3.13-2.6,3.13-4.53,0-1.66-.95-2.95-2.83-3.88-1.89-.93-4.83-1.92-8.81-2.98-4.51-1.19-8.26-2.4-11.24-3.63-2.98-1.23-5.56-3.1-7.71-5.62-2.16-2.52-3.23-5.84-3.23-9.95,0-4.97,1.36-9.33,4.08-13.08,2.72-3.75,6.6-6.65,11.64-8.71,5.04-2.05,10.95-3.08,17.71-3.08,4.97,0,9.62.51,13.93,1.54,4.31,1.03,8.03,2.5,11.14,4.43l-7.16,14.62c-2.72-1.72-5.72-3.03-9-3.93s-6.65-1.34-10.1-1.34c-3.91,0-6.98.63-9.2,1.89-2.22,1.26-3.33,2.89-3.33,4.88,0,1.73.96,3.05,2.88,3.98,1.92.93,4.91,1.92,8.96,2.98,4.51,1.13,8.24,2.29,11.19,3.48s5.49,3.02,7.61,5.47c2.12,2.46,3.19,5.67,3.19,9.65,0,4.91-1.38,9.24-4.13,12.98-2.75,3.75-6.67,6.65-11.74,8.71-5.07,2.06-10.96,3.08-17.66,3.08-5.77,0-11.24-.7-16.42-2.09Z"/>
              <path className="fill-white" d="M86.71,79.2c-5.21-2.59-9.22-6.2-12.04-10.84-2.82-4.64-4.23-9.95-4.23-15.92,0-7.96,1.84-15.12,5.52-21.49,3.68-6.37,8.82-11.34,15.42-14.92,6.6-3.58,14.11-5.37,22.54-5.37,7.03,0,13.15,1.29,18.36,3.88,5.21,2.59,9.22,6.2,12.04,10.84,2.82,4.64,4.23,9.95,4.23,15.92,0,7.96-1.84,15.12-5.52,21.49-3.68,6.37-8.82,11.34-15.42,14.92-6.6,3.58-14.11,5.37-22.54,5.37-7.03,0-13.15-1.29-18.36-3.88ZM118.3,63.48c3.32-2.19,5.87-5.12,7.66-8.81s2.69-7.74,2.69-12.19c0-4.71-1.44-8.47-4.33-11.29-2.89-2.82-6.92-4.23-12.09-4.23-4.38,0-8.22,1.09-11.54,3.28-3.32,2.19-5.87,5.12-7.66,8.81-1.79,3.68-2.69,7.74-2.69,12.19,0,4.71,1.44,8.47,4.33,11.29,2.89,2.82,6.92,4.23,12.09,4.23,4.38,0,8.22-1.09,11.54-3.28Z"/>
              <path className="fill-white" d="M165.56,12.04h19.7l-10.75,54.02h33.23l-3.18,15.62h-52.93l13.93-69.64Z"/>
              <path className="fill-white" d="M229.93,80.04c-4.38-2.02-7.73-4.92-10.05-8.71-2.32-3.78-3.48-8.22-3.48-13.33,0-2.12.26-4.58.8-7.36l7.76-38.6h19.7l-7.66,38.3c-.4,1.99-.6,3.81-.6,5.47,0,7.3,3.85,10.94,11.54,10.94,4.18,0,7.48-1.28,9.9-3.83,2.42-2.55,4.23-6.75,5.42-12.59l7.66-38.3h19.4l-7.86,39.5c-4.25,21.03-16.55,31.54-36.91,31.54-6.04,0-11.24-1.01-15.62-3.03Z"/>
              <path className="fill-white" d="M314.3,27.66h-21.39l3.18-15.62h62.38l-3.18,15.62h-21.29l-10.84,54.02h-19.7l10.84-54.02Z"/>
              <path className="fill-white" d="M365.83,12.04h19.7l-13.93,69.64h-19.7l13.93-69.64Z"/>
              <path className="fill-white" d="M404.88,79.2c-5.21-2.59-9.22-6.2-12.04-10.84-2.82-4.64-4.23-9.95-4.23-15.92,0-7.96,1.84-15.12,5.52-21.49,3.68-6.37,8.82-11.34,15.42-14.92,6.6-3.58,14.11-5.37,22.54-5.37,7.03,0,13.15,1.29,18.36,3.88,5.21,2.59,9.22,6.2,12.04,10.84,2.82,4.64,4.23,9.95,4.23,15.92,0,7.96-1.84,15.12-5.52,21.49-3.68,6.37-8.82,11.34-15.42,14.92-6.6,3.58-14.11,5.37-22.54,5.37-7.03,0-13.15-1.29-18.36-3.88ZM436.47,63.48c3.32-2.19,5.87-5.12,7.66-8.81s2.69-7.74,2.69-12.19c0-4.71-1.44-8.47-4.33-11.29-2.89-2.82-6.92-4.23-12.09-4.23-4.38,0-8.22,1.09-11.54,3.28-3.32,2.19-5.87,5.12-7.66,8.81-1.79,3.68-2.69,7.74-2.69,12.19,0,4.71,1.44,8.47,4.33,11.29,2.89,2.82,6.92,4.23,12.09,4.23,4.38,0,8.22-1.09,11.54-3.28Z"/>
              <path className="fill-white" d="M549.9,12.04l-13.93,69.64h-16.22l-22.98-38.21-7.66,38.21h-19.3l13.93-69.64h16.22l23.08,38.11,7.56-38.11h19.3Z"/>
              <path className="fill-white" d="M559.55,80.99c-5.17-1.39-9.39-3.18-12.64-5.37l7.76-14.73c3.52,2.26,7.26,3.96,11.24,5.12,3.98,1.16,7.96,1.74,11.94,1.74s6.71-.55,8.8-1.64c2.09-1.09,3.13-2.6,3.13-4.53,0-1.66-.95-2.95-2.83-3.88-1.89-.93-4.83-1.92-8.81-2.98-4.51-1.19-8.26-2.4-11.24-3.63-2.99-1.23-5.56-3.1-7.71-5.62-2.16-2.52-3.23-5.84-3.23-9.95,0-4.97,1.36-9.33,4.08-13.08,2.72-3.75,6.6-6.65,11.64-8.71,5.04-2.05,10.94-3.08,17.71-3.08,4.97,0,9.62.51,13.93,1.54,4.31,1.03,8.02,2.5,11.14,4.43l-7.16,14.62c-2.72-1.72-5.72-3.03-9-3.93s-6.65-1.34-10.1-1.34c-3.91,0-6.98.63-9.2,1.89-2.22,1.26-3.33,2.89-3.33,4.88,0,1.73.96,3.05,2.89,3.98,1.92.93,4.91,1.92,8.95,2.98,4.51,1.13,8.24,2.29,11.19,3.48s5.49,3.02,7.61,5.47c2.12,2.46,3.18,5.67,3.18,9.65,0,4.91-1.38,9.24-4.13,12.98-2.75,3.75-6.67,6.65-11.74,8.71-5.07,2.06-10.96,3.08-17.66,3.08-5.77,0-11.24-.7-16.42-2.09Z"/>
              <path fill="url(#logo-gradient-footer)" d="M680.81,66.76h-25.85l14.62-11.35c6.04-4.57,10.26-8.75,12.69-12.53,2.42-3.78,3.63-7.86,3.63-12.24,0-3.65-1.1-6.95-3.29-9.9-2.19-2.95-5.22-5.26-9.1-6.91-3.88-1.66-8.31-2.49-13.28-2.49-6.3,0-12.02,1.28-17.16,3.83-5.15,2.55-9.11,5.98-11.89,10.3l13.13,8.65c1.72-2.25,3.7-3.98,5.92-5.17,2.22-1.2,4.56-1.79,7.01-1.79,2.72,0,4.81.58,6.27,1.74,1.46,1.16,2.19,2.73,2.19,4.72s-.66,3.93-1.99,5.82c-1.33,1.9-3.75,4.2-7.26,6.92l-30.45,23.68-2.48,12.34h48.79l8.5-15.62ZM755.16,28.8l-6.31,12.13,18.92,7.22-27.39,9.05-8.97,17.26,51.88-17.85,3.08-15.32-31.21-12.49ZM752.35,0l-48.75,93.72h-19.7l42.75-78.5h-8.52l6-15.22h28.22ZM680.81,66.76l-8.5,15.62"/>
            </svg>
            <p className="text-gray-400 leading-relaxed">
              Pioneering technological excellence at global events, delivering innovative solutions that transform digital experiences.
            </p>
            <div className="space-y-4">
              <a href="#" className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors duration-300">
                <MapPin className="w-5 h-5 text-accent" />
                <span>Al Wasl 3, Expo City Dubai, Dubai, UAE</span>
              </a>
              <a href="mailto:info@solutions21.ae" className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors duration-300">
                <Mail className="w-5 h-5 text-accent" />
                <span>info@solutions21.ae</span>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-medium text-lg mb-8">Quick Links</h3>
            <ul className="space-y-4">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="group flex items-center text-gray-400 hover:text-white transition-colors duration-300"
                  >
                    <ArrowRight className="w-4 h-4 mr-2 transition-transform duration-300 group-hover:translate-x-1" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h3 className="text-white font-medium text-lg mb-8">Newsletter</h3>
            <p className="text-gray-400 mb-6">
              Subscribe to our newsletter for the latest updates and insights.
            </p>
            <form onSubmit={handleSubscribe} className="space-y-4">
              {status.type && (
                <div className={cn(
                  "p-4 rounded-lg text-sm",
                  status.type === 'success' ? "bg-green-500/10 text-green-500" : "bg-red-500/10 text-red-500"
                )}>
                  {status.message}
                </div>
              )}
              <div>
                <input
                  type="text"
                  placeholder="Full Name"
                  value={formData.fullName}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, fullName: e.target.value }));
                    if (formErrors.fullName) setFormErrors(prev => ({ ...prev, fullName: '' }));
                  }}
                  className={cn(
                    "w-full bg-transparent border-b py-4 text-white placeholder-white/60",
                    formErrors.fullName ? "border-red-500" : "border-white/20",
                    "focus:outline-none focus:border-accent",
                    "transition-colors duration-300"
                  )}
                />
                {formErrors.fullName && (
                  <p className="mt-2 text-sm text-red-500">{formErrors.fullName}</p>
                )}
              </div>
              <div>
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, email: e.target.value }));
                    if (formErrors.email) setFormErrors(prev => ({ ...prev, email: '' }));
                  }}
                  className={cn(
                    "w-full bg-transparent border-b py-4 text-white placeholder-white/60",
                    formErrors.email ? "border-red-500" : "border-white/20",
                    "focus:outline-none focus:border-accent",
                    "transition-colors duration-300"
                  )}
                />
                {formErrors.email && (
                  <p className="mt-2 text-sm text-red-500">{formErrors.email}</p>
                )}
              </div>
              <motion.button
                type="submit"
                disabled={isSubmitting}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={cn(
                  "w-full px-6 py-3 bg-accent text-white rounded-lg font-medium",
                  "hover:bg-accent/90 transition-colors duration-300",
                  "disabled:opacity-50 disabled:cursor-not-allowed"
                )}
              >
                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
              </motion.button>
            </form>
            <div className="mt-8">
              <p className="text-gray-400 mb-4">Connect with us on LinkedIn:</p>
              <a
                href="https://www.linkedin.com/company/solutions-21-fzco"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-3 px-4 py-2 bg-white/5 rounded-lg text-gray-400 hover:bg-accent hover:text-white transition-colors duration-300"
              >
                <Linkedin className="w-5 h-5" />
                <span>Solutions21</span>
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-6 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm">
              © 2025 Solutions21. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;